import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('company', formData.company);
      formBody.append('projectType', formData.projectType);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6f5ccc1tmg2k2pmb6v0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          projectType: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-5 md:pt-32 md:pb-20 md:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-8">함께 일해요</h1>
          <p className="text-base md:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
            진행하고 싶은 프로젝트가 있으신가요? 여러분의 이야기를 듣고 싶습니다. 연락 주시면 함께 멋진 작품을 만들어 보겠습니다.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-12 px-5 md:py-20 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8">메시지 보내기</h2>
              
              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-5 md:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    이름 *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3.5 py-2.5 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    이메일 *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3.5 py-2.5 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    회사명
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-3.5 py-2.5 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    프로젝트 유형 *
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    required
                    value={formData.projectType}
                    onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                    className="w-full px-3.5 py-2.5 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">프로젝트 유형을 선택하세요</option>
                    <option value="illustration">일러스트레이션</option>
                    <option value="design">디자인</option>
                    <option value="writing">작문</option>
                    <option value="multiple">복합 서비스</option>
                    <option value="other">기타</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    메시지 *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-3.5 py-2.5 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1.5">{formData.message.length}/500자</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-3.5 md:py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-sm"
                >
                  {isSubmitting ? '전송 중...' : '메시지 보내기'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 text-sm">감사합니다! 메시지가 성공적으로 전송되었습니다.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm">죄송합니다. 문제가 발생했습니다. 다시 시도해 주세요.</p>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8">연락처</h2>
              
              <div className="space-y-6 md:space-y-8 mb-8 md:mb-12">
                <div className="flex gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-purple-100 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-xl md:text-2xl text-purple-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-0.5 md:mb-1 text-base md:text-lg">이메일</h3>
                    <p className="text-gray-600 text-sm">agencypavane@gmail.com</p>
                  </div>
                </div>

                <div className="flex gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-xl md:text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-0.5 md:mb-1 text-base md:text-lg">오피스</h3>
                    <p className="text-gray-600 text-sm">서울시 강남구 역삼동 704-45</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-3 md:mb-4 text-base md:text-lg">팔로우하기</h3>
                <div className="flex gap-3 md:gap-4">
                  <a href="#" className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer">
                    <i className="ri-instagram-line text-lg md:text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
